import React, { useState } from 'react'
import { Box } from '@mui/material'

import BatchesPanel from '../BatchesPanel'
import { useCodingBatchesPaged } from '../../../hooks/useCodingBatchesPaged'

/**
 * PipelineBatchesSection — the pipeline's coding batches, newest first.
 *
 * A batch is the unit of work the queue actually dispatches, so this is the
 * section that answers "what has this pipeline sent to a model, and what did it
 * cost". It replaces the pipeline's Imports section: receipts are provenance
 * and are now job-wide in the Data tab, while batches are the pipeline's own
 * work and belong here.
 *
 * The table itself lives in BatchesPanel, shared with the job-wide view in the
 * Activity tab. This file is now just the pipeline SCOPE: which endpoint to
 * read, and what the empty state should say.
 *
 * Server-side paging (a long-lived pipeline has hundreds of batches) and a
 * poll that runs ONLY while a row on the visible page is still moving.
 */

interface PipelineBatchesSectionProps {
  customer: string
  job: string
  pipelineId: string
  colorMap?: Record<string, string>
  /** False while the section is not the visible one — a hidden view must not poll. */
  active?: boolean
}

const PipelineBatchesSection: React.FC<PipelineBatchesSectionProps> = ({
  customer,
  job,
  pipelineId,
  colorMap,
  active = true,
}) => {
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)

  const { data, loading, isFetching, error, refetch } = useCodingBatchesPaged(
    customer,
    job,
    pipelineId,
    page,
    rowsPerPage,
    active,
  )

  return (
    <Box sx={{ p: 3 }}>
      <BatchesPanel
        customer={customer}
        job={job}
        title="Coding batches"
        subtitle="One row per batch of records sent to a model lane, newest first."
        emptyText="No coding batches yet. A batch is created the moment records are submitted to this pipeline — there is no separate batching step to switch on."
        data={data}
        loading={loading}
        isFetching={isFetching}
        error={error}
        refetch={refetch}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
        colorMap={colorMap}
      />
    </Box>
  )
}

export default PipelineBatchesSection
